import { useQuery } from '@tanstack/react-query';
import {
  Device,
  DeviceExistBody,
  DeviceType,
  Gateway,
  Beacon,
  ProfileType,
  isImeiNumber,
  isMacAddress,
} from '@ixicare/resources';
import { Panel } from '@ixicare/ui';
import React from 'react';
import { t } from '~/i18n';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { GenericTable } from './GenericTable';
import { Spacer } from '~components/Utils/Spacer';
import { useStores } from '~store/utils';
import { mapDBGWithType } from '~modules/devices/utils/deviceMapping';
import { useDeviceDetail } from '~modules/sites/hooks/useDeviceDetail';
import { useDeviceArchive } from '~modules/sites/hooks/useDeviceArchive';
import { DBGWithType } from '~modules/devices/components/DeviceHealthTable/types';
import { useTableRowActions } from './hooks/useTableRowActions';
import {
  navigateMapAction,
  editAction,
  deleteAction,
  configAction,
  ConfigurableDevice,
  moveAction,
  deleteDeviceDataAction,
} from './deviceTableActions';
import {
  transformButtonToRow,
  transformWearableToRow,
  transformEviewToRow,
  transformGatewayToRow,
  transformBeaconToRow,
  BUTTON_COLUMNS_WITH_TYPE,
  WEARABLE_COLUMNS_WITH_TYPE,
  EVIEW_COLUMNS_WITH_TYPE,
  GATEWAY_COLUMNS_WITH_TYPE,
  BEACON_COLUMNS_WITH_TYPE,
} from './rowTransformers';
import { BaseTableConfig } from './types';
import { DeleteDeviceModal } from '~modules/devices/components/DeviceModals/DeleteDeviceModal';
import { UpdateDeviceModal } from '~modules/devices/components/DeviceModals/UpdateDeviceModal';
import { DeleteDeviceDataModal } from '~modules/devices/components/DeviceModals/DeleteDeviceDataModal';
import { useDecommissionActions } from '~modules/devices/hooks/useDecommissionActions';
import { ScannableSearch, ScanResult } from '~components/DeviceScanner';

type SearchResult = {
  type: 'device' | 'gateway' | 'beacon';
  // Null when the hit is outside the caller's sites — see resolveExisting.
  data: any | null;
  isArchived?: boolean;
  siteId?: number;
};

// The exists endpoints look up globally so a 404 means "no such device" rather
// than "not on your site", but the entity GET is site-scoped and would 403. So
// an out-of-scope hit is reported as existing and nothing more: no detail fetch,
// no owner, no row actions.
async function resolveExisting(
  type: SearchResult['type'],
  exists: DeviceExistBody,
  fetchDetail: (id: number) => Promise<any>
): Promise<SearchResult | null> {
  if (!exists.id) return null;

  const result = { type, isArchived: exists.isArchived, siteId: exists.siteId };
  if (exists.inAllowedSites === false) return { ...result, data: null };

  return { ...result, data: await fetchDetail(exists.id) };
}

async function searchByQuery(query: string): Promise<SearchResult | null> {
  // IMEI identifies cellular devices (eview/twig) — they aren't found by MAC.
  if (isImeiNumber(query)) {
    try {
      return await resolveExisting('device', await Device.deviceImeiNumberExist(query), (id) =>
        Device.getDevice(id, true)
      );
    } catch {
      // Device not found
    }
    return null;
  }

  // Try device first
  try {
    const device = await resolveExisting('device', await Device.deviceMacAddressExist(query), (id) =>
      Device.getDevice(id, true)
    );
    if (device) return device;
  } catch {
    // Device not found, continue
  }

  // Try gateway next
  try {
    const gateway = await resolveExisting('gateway', await Gateway.gatewayMacAddressExist(query), (id) =>
      Gateway.getGateway(id, true)
    );
    if (gateway) return gateway;
  } catch {
    // Gateway not found, continue
  }

  // Try beacon last
  try {
    const beacon = await resolveExisting('beacon', await Beacon.beaconMacAddressExist(query), (id) =>
      Beacon.getBeacon(id, true)
    );
    if (beacon) return beacon;
  } catch {
    // Beacon not found
  }

  return null;
}

const SEARCH_PARAM = 'q';

export function DeviceFinderTable({ siteId }: { siteId?: number }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { sites, device: deviceStore, notifications, auth, permissions } = useStores();

  const [query, setQuery] = React.useState(() => searchParams.get(SEARCH_PARAM) ?? '');
  const isValid = isMacAddress(query) || isImeiNumber(query);

  const isEmployee = auth.userType === ProfileType.WatcherrEmployee;
  const [dataDevices, setDataDevices] = React.useState<DBGWithType[]>([]);

  // Sync the query into the URL so a search can be hard-linked. setSearchParams
  // strips the tab hash, so restore it the same way the archive filter does.
  const handleSearchChange = (value: string) => {
    setQuery(value);
    const currentHash = window.location.hash;
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(SEARCH_PARAM, value);
    } else {
      newParams.delete(SEARCH_PARAM);
    }
    setSearchParams(newParams, { replace: true });
    if (currentHash) {
      window.history.replaceState(null, '', window.location.pathname + '?' + newParams.toString() + currentHash);
    }
  };

  const { data, refetch } = useQuery({
    queryKey: ['search', query],
    queryFn: () => searchByQuery(query!),
    enabled: isValid,
  });

  // Found, but on a site this profile has no access to — nothing beyond its
  // existence is known, so the table has nothing to render.
  const isOutOfScope = data != null && data.data == null;

  // Get the site name if device is found in a different site
  const foundInOtherSite = React.useMemo(() => {
    if (!data || !data.siteId || !siteId) return null;
    if (data.siteId === siteId) return null;

    const site = sites.sites.find((s) => s.id === data.siteId);
    return site ? site.name : null;
  }, [data, siteId, sites.sites]);

  // Site uuid of the device itself (may differ from the selected site) — needed
  // for the map action which is scoped by site.
  const deviceSiteUuid = React.useMemo(() => {
    if (!data?.siteId) return undefined;
    return sites.sites.find((s) => s.id === data.siteId)?.uuid;
  }, [data, sites.sites]);

  // Dynamic columns based on search result type
  const columns = React.useMemo(() => {
    if (!data?.data) return [];

    if (data.type === 'device') {
      if (data.data.deviceType === DeviceType.WALL_MOUNTED_DISTRESS_BUTTON) {
        return BUTTON_COLUMNS_WITH_TYPE;
      }
      if ([DeviceType.EVIEW, DeviceType.TWIG].includes(data.data.deviceType)) {
        return EVIEW_COLUMNS_WITH_TYPE;
      }
      return WEARABLE_COLUMNS_WITH_TYPE;
    } else if (data.type === 'gateway') {
      return GATEWAY_COLUMNS_WITH_TYPE;
    } else if (data.type === 'beacon') {
      return BEACON_COLUMNS_WITH_TYPE;
    }
    return [];
  }, [data]);

  const rows = React.useMemo(() => {
    if (!data?.data) return [];

    const context = {
      isArchived: data.isArchived,
      siteId: data.siteId,
      siteUuid: deviceSiteUuid,
      showTypeColumn: true,
    };

    if (data.type === 'device') {
      if (data.data.deviceType === DeviceType.WALL_MOUNTED_DISTRESS_BUTTON) {
        return [transformButtonToRow(data.data, context)];
      }
      if ([DeviceType.EVIEW, DeviceType.TWIG].includes(data.data.deviceType)) {
        return [transformEviewToRow(data.data, context)];
      }
      return [transformWearableToRow(data.data, context)];
    } else if (data.type === 'gateway') {
      return [transformGatewayToRow(data.data, context)];
    } else if (data.type === 'beacon') {
      return [transformBeaconToRow(data.data, context)];
    }
    return [];
  }, [data, deviceSiteUuid]);

  // Single-row equivalent of the device tables' rows, so the shared row actions
  // (map / config / edit / archive) can operate on the found device.
  const devicesWithType = React.useMemo(() => {
    if (!data?.data) return [];
    const type =
      data.type === 'gateway'
        ? DeviceType.GATEWAY
        : data.type === 'beacon'
          ? DeviceType.BEACON
          : (data.data.deviceType as DeviceType);
    return mapDBGWithType([data.data], type);
  }, [data]);

  const { selectedDevices, setSelectedDevices, selectedDeviceDetail, handleCloseDetail } =
    useDeviceDetail(devicesWithType);

  const { devicesToArchive, setDevicesToArchive, deleteMutation } = useDeviceArchive({
    onRefresh: refetch,
    notifications,
  });

  const { actions: decommissionActions, modals: decommissionModals } = useDecommissionActions({
    isEmployee,
    onRefresh: refetch,
  });

  const onConfigAction = (devices: ConfigurableDevice[]) => {
    const dbg = devices[0];
    if (!dbg) return;
    deviceStore.hydrate({
      macAddress: dbg.macAddress,
      deviceType: dbg.type,
    });
    navigate(`/device-overview/${dbg.uuid}/configuration`);
  };

  const openDeviceModal = (dbgs: DBGWithType[]) => setSelectedDevices(dbgs);

  const { actionProps, onActionClick, isActionVisible } = useTableRowActions(
    devicesWithType,
    !data?.data || data.isArchived
      ? [moveAction(openDeviceModal), ...decommissionActions]
      : [
          ...(data.type === 'device'
            ? [
                ...(data.data.deviceType === DeviceType.WALL_MOUNTED_DISTRESS_BUTTON
                  ? [navigateMapAction(navigate, deviceSiteUuid, false, (dbg: DBGWithType) => dbg.type)]
                  : []),
                ...(permissions.has('can_edit_all_devices') ? [configAction(onConfigAction)] : []),
              ]
            : [navigateMapAction(navigate, deviceSiteUuid, false, (dbg: DBGWithType) => dbg.type)]),
          editAction(openDeviceModal),
          ...(isEmployee ? [deleteDeviceDataAction<DBGWithType>((dbgs) => setDataDevices(dbgs))] : []),
          deleteAction((dbgs) => setDevicesToArchive(dbgs)),
        ]
  );

  const tableConfig: BaseTableConfig = {
    tableId: 'device-finder',
    columns,
    columnIds: [],
  };

  return (
    <>
      <ScannableSearch
        placeholder="MAC address or IMEI, e.g. EA:89:89:84:14:2F"
        onSearchChange={handleSearchChange}
        value={query}
        // The finder looks up by MAC or IMEI, so drop the type number a device
        // QR carries and search on the identifier alone.
        onScan={(result: ScanResult) => handleSearchChange(result.imeiNumber ?? result.macAddress ?? result.raw)}
      />
      {isOutOfScope ? (
        <>
          <Spacer size={35} />
          <Panel.Title>{t('device.form.infobox.otherSite')}</Panel.Title>
        </>
      ) : rows.length != 0 ? (
        <>
          <Spacer size={35} />
          {foundInOtherSite && (
            <>
              <Panel.Title>Found in other site: {foundInOtherSite}</Panel.Title>
              <Spacer size={15} />
            </>
          )}
          <GenericTable
            config={tableConfig}
            rows={rows ?? []}
            actions={actionProps}
            onActionClick={onActionClick}
            isActionVisible={isActionVisible}
          />
        </>
      ) : (
        <></>
      )}
      {selectedDevices && selectedDeviceDetail && (
        <UpdateDeviceModal
          type={selectedDeviceDetail.type}
          devices={selectedDevices}
          onClose={handleCloseDetail}
          onRefresh={refetch}
          showSiteSelect={true}
        />
      )}
      {devicesToArchive.length > 0 && (
        <DeleteDeviceModal
          devices={devicesToArchive}
          showHardDelete={isEmployee}
          onClose={() => setDevicesToArchive([])}
          onDelete={(hardDelete) => deleteMutation.mutate(hardDelete)}
          isPending={deleteMutation.isPending}
        />
      )}
      {dataDevices.length > 0 && (
        <DeleteDeviceDataModal devices={dataDevices} onClose={() => setDataDevices([])} onRefresh={refetch} />
      )}
      {decommissionModals}
    </>
  );
}
